import {
  AlertTriangle,
  ArrowRight,
  Ban,
  Code,
  CreditCard,
  FileText,
  Globe,
  Lock,
  Scale,
  Shield,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

const sections = [
  {
    num: "1",
    icon: FileText,
    title: "Acceptance of Terms",
    content: `By accessing or using any service provided by Furistic Technologies Inc. ("Furistic," "we," "us," or "our"), including but not limited to RealtyOS, TradeOS, ConformOS, Haulyard, Foodium, our APIs, websites, and any related services (collectively, the "Platform"), you agree to be bound by these Terms of Service ("Terms"). If you are using the Platform on behalf of an organization, you represent and warrant that you have authority to bind that organization to these Terms.

These Terms constitute a legally binding agreement between you and Furistic Technologies Inc. If you do not agree to these Terms, you must not access or use the Platform.`,
  },
  {
    num: "2",
    icon: Users,
    title: "Account Creation & Access",
    content: `To access the Platform, you must create a Furistic account through our centralized identity system. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.

You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate. Furistic reserves the right to suspend or terminate accounts that contain inaccurate or fraudulent information.

Single sign-on (SSO) access across Furistic products (RealtyOS, TradeOS, ConformOS, Haulyard, Foodium, and future products) is managed through our unified identity platform. Access to individual products may require separate subscriptions or licenses.`,
  },
  {
    num: "3",
    icon: Globe,
    title: "Product-Specific Terms",
    content: `The Platform encompasses multiple products, each of which may be subject to additional product-specific terms:

**RealtyOS** — Our AI-powered operating system for real estate professionals. Use of RealtyOS is subject to the RealtyOS Product Terms, which govern real estate data handling, MLS integrations, and industry-specific compliance requirements.

**TradeOS** — Our AI-powered operating system for trading professionals. Use of TradeOS is subject to the TradeOS Product Terms, which govern financial data handling, market data usage, and trading-specific regulatory requirements.

**ConformOS, Haulyard, and Foodium** — Our standards-audit, waste-services, and restaurants products. Use of these products is subject to their product-specific terms where applicable.

Product-specific terms are incorporated by reference into these Terms. In the event of a conflict between these Terms and any product-specific terms, the product-specific terms shall prevail with respect to the applicable product.`,
  },
  {
    num: "4",
    icon: Ban,
    title: "Acceptable Use",
    content: `You agree not to use the Platform to:

• Violate any applicable law, regulation, or third-party right
• Transmit malware, viruses, or any code of a destructive nature
• Attempt to gain unauthorized access to any part of the Platform or its infrastructure
• Use automated tools to scrape, crawl, or extract data from the Platform without express written consent
• Reverse engineer, decompile, or disassemble any aspect of the Platform
• Use the Platform to generate, store, or transmit content that is unlawful, harmful, threatening, abusive, or otherwise objectionable
• Circumvent any access controls, rate limits, or usage restrictions
• Resell, sublicense, or redistribute Platform access without authorization
• Use AI-generated outputs from the Platform to mislead others about the nature of the content
• Interfere with or disrupt the integrity or performance of the Platform

Furistic reserves the right to investigate and take appropriate action against any violation of this section, including suspension or termination of access.`,
  },
  {
    num: "5",
    icon: Shield,
    title: "Intellectual Property",
    content: `**Platform IP.** The Platform, including all AI models, algorithms, software, designs, text, graphics, and other content provided by Furistic, is owned by Furistic Technologies Inc. and protected by intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to use the Platform in accordance with these Terms.

**Your Content.** You retain ownership of any data, content, or materials you submit to the Platform ("Your Content"). By submitting Your Content, you grant Furistic a limited license to process, store, and display Your Content as necessary to provide the Platform services.

**AI Models.** All AI models, trained weights, fine-tuned parameters, and model architectures used in the Platform are the exclusive property of Furistic Technologies Inc. No rights to our AI models are transferred through use of the Platform.

**Feedback.** Any feedback, suggestions, or ideas you provide regarding the Platform may be used by Furistic without obligation or compensation to you.`,
  },
  {
    num: "6",
    icon: CreditCard,
    title: "Fees & Payment",
    content: `Access to the Platform may require payment of fees, which vary by product and subscription tier. Fees for each product (RealtyOS, TradeOS, ConformOS, Haulyard, Foodium) are set independently and are detailed on the respective product pricing pages.

• All fees are quoted in U.S. dollars unless otherwise specified
• Subscription fees are billed in advance on a monthly or annual basis
• Usage-based fees (API calls, AI processing, storage) are billed in arrears
• All fees are non-refundable except as expressly stated in these Terms or required by applicable law
• Furistic may modify fees upon 30 days' written notice; continued use after the effective date constitutes acceptance

Failure to pay fees when due may result in suspension or termination of your access to the Platform.`,
  },
  {
    num: "7",
    icon: Code,
    title: "API Usage",
    content: `Access to Furistic APIs is subject to applicable rate limits, usage quotas, and the API-specific documentation. You agree to:

• Comply with all API documentation, including authentication requirements and rate limits
• Not exceed your allocated API usage quotas without prior authorization
• Secure your API keys and tokens, and not share them with unauthorized parties
• Not use the API to build a competing product or service
• Include proper attribution where required by the API documentation

Furistic reserves the right to modify, deprecate, or discontinue any API endpoint with reasonable notice. We will make commercially reasonable efforts to provide at least 90 days' notice for breaking changes to stable API endpoints.`,
  },
  {
    num: "8",
    icon: AlertTriangle,
    title: "Limitation of Liability",
    content: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, FURISTIC INC. AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:

• Your access to or use of (or inability to access or use) the Platform
• Any conduct or content of any third party on the Platform
• Any AI-generated content or recommendations provided through the Platform
• Unauthorized access, use, or alteration of your transmissions or content

FURISTIC'S TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE PLATFORM SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO FURISTIC IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED CANADIAN DOLLARS (CAD $100.00).`,
  },
  {
    num: "9",
    icon: Scale,
    title: "Termination",
    content: `You may terminate your account at any time by contacting us at legal@furistic.co or through your account settings. Furistic may suspend or terminate your access to the Platform at any time for any reason, including but not limited to a violation of these Terms.

Upon termination:
• Your right to access and use the Platform will immediately cease
• Furistic will make your data available for export for a period of 30 days following termination, after which it may be deleted
• Any outstanding fees will become immediately due and payable
• Provisions that by their nature should survive termination (including intellectual property, limitation of liability, and governing law) will survive

Furistic may also terminate or suspend access to the Platform immediately, without prior notice, if we reasonably believe you have violated these Terms or applicable law.`,
  },
  {
    num: "10",
    icon: Scale,
    title: "Governing Law & Dispute Resolution",
    content: `These Terms shall be governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of law principles.

Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of the Province of Ontario, located in the City of Toronto.

Before initiating any formal proceedings, you agree to attempt to resolve disputes informally by contacting us at legal@furistic.co. If the dispute is not resolved within 30 days, either party may proceed with formal proceedings.`,
  },
];

export default function TermsPage() {
  return (
    <main className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <section className="relative pt-32 pb-16 bg-[#09090b]">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-950/20 to-transparent" />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 text-sm mb-6">
              <FileText className="w-4 h-4" />
              Legal
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-display text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-zinc-400">
              Last Updated: March 18, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Intro callout */}
          <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800 mb-8">
            <div className="border-l-4 border-violet-500 pl-6">
              <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                These Terms of Service govern your use of the Furistic platform
                and all associated products, including RealtyOS, TradeOS,
                ConformOS, Haulyard, and Foodium. Please read them carefully
                before using our services. By accessing or using any Furistic
                product, you agree to be bound by these Terms.
              </p>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.num}
                  className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                      <span className="text-sm font-bold text-violet-400">
                        {section.num}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-violet-400" />
                      <h2 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-white">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                  <div className="text-zinc-700 dark:text-zinc-300 leading-relaxed whitespace-pre-line pl-14">
                    {section.content}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contact */}
          <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800 mt-8">
            <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-4">
              Questions?
            </h2>
            <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
              If you have any questions about these Terms of Service, please
              contact us at{" "}
              <a
                href="mailto:legal@furistic.co"
                className="text-violet-400 hover:text-violet-300 underline"
              >
                legal@furistic.co
              </a>
              .
            </p>
          </div>

          {/* Other Legal Pages */}
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider mb-4">
              Other Legal Pages
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                {
                  href: "/legal/privacy",
                  label: "Privacy Policy",
                  icon: Shield,
                },
                {
                  href: "/legal/disclaimer",
                  label: "AI Disclaimer",
                  icon: AlertTriangle,
                },
                {
                  href: "/legal/security",
                  label: "Security & Infrastructure",
                  icon: Lock,
                },
              ].map((link) => {
                const LIcon = link.icon;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-3 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-violet-500/30 transition group"
                  >
                    <LIcon className="w-4 h-4 text-violet-400" />
                    <span className="text-sm text-zinc-300 group-hover:text-white transition">
                      {link.label}
                    </span>
                    <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-violet-400 ml-auto transition" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
